"use client";

import Link from "next/link";
import {
  COAST_COMPARISONS,
  MOUNTAIN_COMPARISONS,
  compareElevation,
  distinctComparisons,
} from "@/lib/climate/comparisons";
import { useClimateState } from "./use-climate-state";
import { CopyViewLink } from "./view-link";
import { Sources, Segments } from "./ui";
import s from "./climate.module.css";
import c from "./place-comparison.module.css";

function MountainComparison() {
  const ids = MOUNTAIN_COMPARISONS.map((item) => item.id);
  const [first, setFirst] = useClimateState<string>(
    "compareA",
    "north-alps",
    ids,
  );
  const [second, setSecond] = useClimateState<string>(
    "compareB",
    "south-alps",
    ids,
  );
  const [third, setThird] = useClimateState<string>(
    "compareC",
    "french-pyrenees",
    ids,
  );
  const [altitude, setAltitude] = useClimateState("compareAltitude", "2000", [
    "1000",
    "1500",
    "2000",
    "2500",
    "3000",
  ]);
  const selected = distinctComparisons([first, second, third], ids);
  const setters = [setFirst, setSecond, setThird];
  return (
    <>
      <div className={c.context}>
        <div>
          <strong>2030–2050</strong>
          <p>Groomed natural snow, across RCP2.6 / 4.5 / 8.5</p>
        </div>
        <label className={s.field}>
          Compare terrain at
          <select
            value={altitude}
            onChange={(event) =>
              setAltitude(event.target.value as typeof altitude)
            }
          >
            {["1000", "1500", "2000", "2500", "3000"].map((value) => (
              <option key={value} value={value}>
                {Number(value).toLocaleString("en-GB")} m
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className={c.columns}>
        {selected.map((id, index) => {
          const region = MOUNTAIN_COMPARISONS.find((item) => item.id === id)!;
          const position = compareElevation(
            Number(altitude),
            region.elevation.low,
            region.elevation.high,
          );
          const y = (metres: number) => 264 - (metres / 3500) * 230;
          return (
            <article className={c.place} key={index}>
              <label className={s.field}>
                Mountain region {index + 1}
                <select
                  value={id}
                  onChange={(event) => setters[index](event.target.value)}
                >
                  {MOUNTAIN_COMPARISONS.map((item) => (
                    <option
                      key={item.id}
                      value={item.id}
                      disabled={selected.includes(item.id) && item.id !== id}
                    >
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <svg
                viewBox="0 0 330 300"
                className={c.mountain}
                role="img"
                aria-label={`${region.name}: regional reliability elevation ${region.elevation.low} to ${region.elevation.high} metres; ${altitude} metres is ${position} this range. Conceptual terrain.`}
              >
                <defs>
                  <clipPath id={`compare-mountain-${index}`}>
                    <path d="M65 264 188 34 315 264Z" />
                  </clipPath>
                </defs>
                <path d="M65 264 188 34 315 264Z" fill="#dce9e5" />
                <g clipPath={`url(#compare-mountain-${index})`}>
                  <rect
                    x="60"
                    y={y(region.elevation.low)}
                    width="260"
                    height="250"
                    fill="#a6c5bd"
                  />
                  <rect
                    x="60"
                    y={y(region.elevation.high)}
                    width="260"
                    height={y(region.elevation.low) - y(region.elevation.high)}
                    fill="#16808f"
                  />
                </g>
                {[1000, 2000, 3000].map((tick) => (
                  <g key={tick}>
                    <path
                      d={`M55 ${y(tick)}H320`}
                      stroke="#9eafae"
                      strokeDasharray="2 5"
                    />
                    <text x="2" y={y(tick) + 4} fontSize="11" fill="#576474">
                      {tick} m
                    </text>
                  </g>
                ))}
                <path
                  d={`M55 ${y(Number(altitude))}H320`}
                  stroke="#16233b"
                  strokeWidth="2"
                />
                <circle
                  cx="188"
                  cy={y(Number(altitude))}
                  r="5"
                  fill="#16233b"
                  stroke="white"
                  strokeWidth="2"
                />
                <path d="M65 264 188 34 315 264" fill="none" stroke="#668d88" />
                <text
                  x="170"
                  y="288"
                  textAnchor="middle"
                  fontSize="11"
                  fill="#576474"
                >
                  Conceptual elevation profile
                </text>
              </svg>
              <div className={c.stat}>
                {region.elevation.low.toLocaleString("en-GB")}–
                {region.elevation.high.toLocaleString("en-GB")} <small>m</small>
              </div>
              <p className={c.statLabel}>Regional reliability elevation</p>
              <p className={c.result} aria-live="polite">
                <strong>{Number(altitude).toLocaleString("en-GB")} m</strong> is{" "}
                {position} this study’s range.
              </p>
              <p>{region.interpretation}</p>
              <Link href={`/climate/${region.guide}`}>
                Read the regional evidence
              </Link>
              {region.place && (
                <Link href={`/climate?location=${region.place}#your-location`}>
                  Explore Morzine’s recent seasons
                </Link>
              )}
            </article>
          );
        })}
      </div>
      <div className={c.method}>
        <span className={c.bandKey} /> Teal band: regional medians across three
        scenarios. <span className={c.lineKey} /> Dark line: your selected
        terrain elevation.
      </div>
      <p className={s.note}>
        The study’s criterion is at least 100 days with 100 kg/m² of snow
        between 15 December and 15 April in seven winters out of ten. Being
        above a regional band does not guarantee snow on an individual slope.
        These are scenario spreads, not uncertainty percentiles. No snowmaking
        is included here.
      </p>
      <Sources ids={["elevation"]} />
    </>
  );
}

function CoastComparison() {
  const ids = COAST_COMPARISONS.map((item) => item.id);
  const [first, setFirst] = useClimateState("coastA", "atlantic", ids);
  const [second, setSecond] = useClimateState("coastB", "southern", ids);
  const [third, setThird] = useClimateState("coastC", "east-pacific", ids);
  const selected = distinctComparisons([first, second, third], ids);
  const setters = [setFirst, setSecond, setThird];
  return (
    <>
      <div className={c.context}>
        <div>
          <strong>Late-century ocean outlook</strong>
          <p>
            Direction of mean significant wave-height change under RCP8.5, a
            high emissions pathway
          </p>
        </div>
      </div>
      <div className={c.columns}>
        {selected.map((id, index) => {
          const region = COAST_COMPARISONS.find((item) => item.id === id)!;
          return (
            <article className={c.place} key={index}>
              <label className={s.field}>
                Ocean region {index + 1}
                <select
                  value={id}
                  onChange={(event) => setters[index](event.target.value)}
                >
                  {COAST_COMPARISONS.map((item) => (
                    <option
                      key={item.id}
                      value={item.id}
                      disabled={selected.includes(item.id) && item.id !== id}
                    >
                      {item.name}
                    </option>
                  ))}
                </select>
              </label>
              <div className={c.oceanVisual} aria-hidden="true">
                <span>{region.direction === "increase" ? "↗" : "↘"}</span>
                <svg viewBox="0 0 300 110">
                  <path
                    d="M0 65Q25 5 50 65T150 65T250 65T350 65"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  />
                  <path
                    d="M0 90Q25 30 50 90T150 90T250 90T350 90"
                    fill="none"
                    stroke="currentColor"
                    opacity=".25"
                    strokeWidth="2"
                  />
                </svg>
              </div>
              <h3>Mean wave heights {region.direction}</h3>
              <p className={c.statLabel}>{region.places}</p>
              <p>{region.interpretation}</p>
              <Link href="/climate/surfing">Read the surfing evidence</Link>
            </article>
          );
        })}
      </div>
      <p className={s.note}>
        Arrows show the direction of regional offshore change, not its magnitude
        or a future surf score. Local bathymetry, swell direction, tides, wind
        and coastal change must be assessed separately. These studies do not
        establish which beach will have the best surf in 2050.
      </p>
      <Sources ids={["waveRegions", "waves"]} />
    </>
  );
}

export function PlaceComparison() {
  const [sport, setSport] = useClimateState("compareSport", "ski", [
    "ski",
    "surf",
  ]);
  return (
    <section id="compare" className={`${s.section} ${c.section}`}>
      <div className={s.wrap}>
        <div className={c.heading}>
          <div>
            <span className={c.label}>Place matters</span>
            <h2>
              One planet.
              <br />
              Very different seasons.
            </h2>
            <p>
              Put three regions side by side. See where the science draws a
              difference, then explore what it means on the ground.
            </p>
          </div>
          <Segments
            label="Comparison sport"
            value={sport}
            onChange={setSport}
            options={[
              { value: "ski", label: "Compare mountains" },
              { value: "surf", label: "Compare oceans" },
            ]}
          />
        </div>
        <div hidden={sport !== "ski"}>
          <MountainComparison />
        </div>
        <div hidden={sport !== "surf"}>
          <CoastComparison />
        </div>
        <CopyViewLink anchor="compare" />
      </div>
    </section>
  );
}
